import { FC, useCallback } from "react";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { taskManagementInitiateEmptyTaskThunk } from "@/store/task-management/thunks/form/taskManagementInitiateEmptyTaskThunk";
import { setTaskManagementTaskTarget } from "@/store/task-management/taskManagementSlice";
import {
  NullableEmbeddedTask,
  ResourceTarget,
} from "@/services/cloudchipr.api";
import { AllTypeResourceItem } from "@/store/resources-grid/utils/types/resources";
import { isResourceKubernetes } from "@/store/resources-grid/utils/helpers/isResourceKubernetes";
import { getTaskNameByResourceType } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/utils/helpers/getTaskNameByResourceType";
import { ResourceDataType } from "@/store/live-usage-mgmt/utils/types/types";

interface LiveUsageResourceGridCreateTaskActionProps {
  inRowAction?: boolean;
  disabled?: boolean;
  onClose?(): void;
  masterGridId: string;
}

export const LiveUsageResourceGridCreateTaskAction: FC<
  LiveUsageResourceGridCreateTaskActionProps
> = ({ masterGridId, inRowAction, onClose, disabled }) => {
  const dispatch = useAppDispatch();

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const clickHandler = useCallback(async () => {
    if (!selectedResources?.length) {
      return;
    }

    await dispatch(taskManagementInitiateEmptyTaskThunk());
    dispatch(
      setTaskManagementTaskTarget({
        type: "resource",
        existingTasks: getExistingTasksFromResources(selectedResources),
        resourcesMasterGridId: masterGridId,
        resources: selectedResources.map((resource) => {
          const taskName = getTaskNameByResourceType({
            resource,
          } as ResourceDataType);

          if (!resource) {
            return;
          }

          if (isResourceKubernetes(resource)) {
            return;
          }

          return {
            state: "live",
            id: resource.id,
            name: taskName ?? "",
            tags: resource.tags,
            link: resource.link,
            account: resource.account,
            resource_type: resource.resource_type,
            region: resource.region ?? "",
            price_per_month: `${resource.price_per_month}`,
            provider_identifier: resource.provider_unique_identifier,
            creation_data: resource.creation_data ?? {},
            live_resource_link_response: null,
            forecasted_end_of_month: resource.forecasted_end_of_month,
          };
        }) as ResourceTarget[],
      }),
    );

    onClose?.();
  }, [dispatch, masterGridId, onClose, selectedResources]);

  if (inRowAction) {
    return (
      <MenuItem dense onClick={clickHandler} disabled={disabled}>
        <ListItemIcon sx={{ "& svg": { fontSize: "24px !important" } }}>
          <PostAddOutlinedIcon color="primary" />
        </ListItemIcon>

        <ListItemText>Create Task </ListItemText>
      </MenuItem>
    );
  }

  return (
    <IconButton sx={{ p: 0.5 }} onClick={clickHandler} disabled={disabled}>
      <PostAddOutlinedIcon color={disabled ? "disabled" : "primary"} />
    </IconButton>
  );
};

const getExistingTasksFromResources = (
  resources: (AllTypeResourceItem | undefined)[],
): Record<string, NullableEmbeddedTask> => {
  return resources
    .filter(
      (resource) =>
        resource && !isResourceKubernetes(resource) && !!resource.task,
    )
    .reduce(
      (acc, resource) => {
        if (!resource) {
          return acc;
        }

        if (!isResourceKubernetes(resource)) {
          const id = resource.provider_unique_identifier ?? resource.id;

          acc[id] = resource.task;
        }

        return acc;
      },
      {} as Record<string, NullableEmbeddedTask>,
    );
};
